import React, { useEffect, useState } from 'react';
import axios from '../axiosInstance';
import { FiEdit2, FiSave, FiAlertCircle, FiX, FiLoader, FiCheck, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

export default function BlockStock() {
  const [purposes, setPurposes] = useState([]);
  const [states, setStates] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [filteredWarehouses, setFilteredWarehouses] = useState([]);
  const [selected, setSelected] = useState({
    purpose_id: '',
    state_id: '',
    warehouse_id: '',
    whr_id: '',
  });

  // Extra filters (similar to WarehouseStockList)
  const [commodities, setCommodities] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [seasons, setSeasons] = useState([]);

  const [selectedCommodity, setSelectedCommodity] = useState('');
  const [selectedScheme, setSelectedScheme] = useState('');
  const [selectedSeason, setSelectedSeason] = useState('');

  const [commoditySearch, setCommoditySearch] = useState('');
  const [schemeSearch, setSchemeSearch] = useState('');
  const [seasonSearch, setSeasonSearch] = useState('');

  const [commodityDropdownOpen, setCommodityDropdownOpen] = useState(false);
  const [schemeDropdownOpen, setSchemeDropdownOpen] = useState(false);
  const [seasonDropdownOpen, setSeasonDropdownOpen] = useState(false);

  const [tableData, setTableData] = useState([]);
  const [editRows, setEditRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState('');
  const [tableError, setTableError] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Selection state
  const [selectedRows, setSelectedRows] = useState(new Set()); // Set of whr_id or unique key

  const token = localStorage.getItem('token');

  // Searchable dropdown controls for purpose, state, warehouse
  const [purposeSearch, setPurposeSearch] = useState('');
  const [purposeDropdownOpen, setPurposeDropdownOpen] = useState(false);
  const [stateSearch, setStateSearch] = useState('');
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const [warehouseDropdownOpen, setWarehouseDropdownOpen] = useState(false);

  // Fetch dropdowns on mount
  useEffect(() => {
    const fetchDropdowns = async () => {
      setLoading(true);
      try {
        const [purp, st, wh, comm, schm, seas] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BASE_URL}purposes?paginate=false`),
          axios.get(`${process.env.REACT_APP_BASE_URL}states`),
          axios.get(
            `${process.env.REACT_APP_BASE_URL}warehouse?paginate=false&has_inventory=true`
          ),
          axios.get(`${process.env.REACT_APP_BASE_URL}commodity`),
          axios.get(`${process.env.REACT_APP_BASE_URL}schemes`),
          axios.get(`${process.env.REACT_APP_BASE_URL}seasons?paginate=false`),
        ]);
        setPurposes(purp.data || []);
        setStates(st.data || []);
        setWarehouses(wh.data || []);
        setCommodities(comm.data || []);
        setSchemes(schm.data || []);
        setSeasons(seas.data || []);
      } catch (err) {
        setError('Failed to load dropdowns. Please refresh.');
      }
      setLoading(false);
    };
    fetchDropdowns();
    // eslint-disable-next-line
  }, []);

  // Filter warehouses by state
  useEffect(() => {
    if (selected.state_id) {
      setFilteredWarehouses(
        warehouses.filter((w) => w.state?.id === selected.state_id)
      );
    } else {
      setFilteredWarehouses([]);
    }
  }, [selected.state_id, warehouses]);

  // Fetch table data when warehouse or extra filters are selected
  useEffect(() => {
    if (selected.warehouse_id) {
      fetchTableData(1);
    } else {
      setTableData([]);
      setTotalPages(1);
    }
    // eslint-disable-next-line
  }, [
    selected.warehouse_id,
    selectedCommodity,
    selectedScheme,
    selectedSeason,
    selected.whr_id,
    limit,
  ]);

  // Fetch inventory table
  const fetchTableData = async (pg = 1) => {
    setTableLoading(true);
    setTableError('');
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}stock-blocking/inventory?block_stock=true&page=${pg}&limit=${limit}&warehouse_id=${selected.warehouse_id}&whr_id=${selected.whr_id}&state_id=${selected.state_id}${selectedCommodity ? `&commodity_id=${selectedCommodity}` : ''}${selectedScheme ? `&scheme_id=${selectedScheme}` : ''}${selectedSeason ? `&season_id=${selectedSeason}` : ''}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setTableData(res.data.data || []);
      setTotal(res.data.total || 0);
      setTotalPages(Math.ceil((res.data.total || 0) / (res.data.limit || limit)));
      setPage(pg);
    } catch (err) {
      setTableError(
        err.response?.data?.message ||
        err.message ||
        'Failed to fetch inventory data'
      );
      setTableData([]);
    }
    setTableLoading(false);
  };

  // Toggle single row selection
  const handleSelectRow = (id) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Toggle select all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = tableData.map((row) => row.id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  // Calculate total selected available quantity
  const calculateTotalSelectedQty = () => {
    return tableData
      .filter((row) => selectedRows.has(row.id))
      .reduce((sum, row) => sum + parseFloat(row.remaining_qty || 0), 0)
      .toFixed(2);
  };

  // Calculate total selected available bags
  const calculateTotalSelectedBags = () => {
    return tableData
      .filter((row) => selectedRows.has(row.id))
      .reduce((sum, row) => sum + parseFloat(row.bags || 0), 0)
      .toFixed(0);
  };

  // Handle dropdown change
  const handleSelect = (e) => {
    setSelected({
      ...selected,
      [e.target.name]: e.target.value,
      ...(e.target.name === 'state_id' ? { warehouse_id: '' } : {}),
    });
  };

  // Handle block_qty edit
  // Handle edit for qty or bags
  const handleEdit = (id, field, value) => {
    setEditRows(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }));
  };

  // Bulk Block State
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState(null); // { success: [], failed: [], message: '' }
  const [showBulkModal, setShowBulkModal] = useState(false);

  // Handle Bulk Block
  const handleBulkBlock = async () => {
    if (selectedRows.size === 0) return;
    if (!selected.purpose_id) {
      setError('Please select a Purpose before blocking stock.');
      return;
    }

    setBulkLoading(true);
    setError('');

    try {
      const whrIds = tableData
        .filter(row => selectedRows.has(row.id))
        .map(row => row.whr_id); // Assuming API needs whr_id string

      const payload = {
        whr_ids: whrIds,
        purpose_id: selected.purpose_id,
        state_id: selected.state_id,
        warehouse_id: selected.warehouse_id
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}stock-blocking/bulk`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setBulkResults(response.data.results);
      setShowBulkModal(true);

      // Refresh table if there were any successes
      if (response.data.results?.success?.length > 0) {
        fetchTableData(page);
        setSelectedRows(new Set()); // Clear selection
      }

    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.message ||
        'Failed to process bulk blocking'
      );
    } finally {
      setBulkLoading(false);
    }
  };

  // Save block_qty (dummy API call, replace with your actual API)
  const handleSave = async (row) => {
    setTableError('');
    try {
      setTableLoading(true);
      const payload = {
        whr_id: row.whr_id,
        block_qty: parseFloat(editRows[row.id]?.block_qty || 0),
        bags: parseFloat(editRows[row.id]?.blocked_bags || 0),
        purpose_id: selected.purpose_id,
        state_id: selected.state_id,
        warehouse_id: selected.warehouse_id
      };
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}stock-blocking`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      // Optionally, show a success message or clear the input
      setEditRows((prev) => {
        const newState = { ...prev };
        delete newState[row.id];
        return newState;
      });
      fetchTableData(page);
    } catch (err) {
      setTableError(
        err.response?.data?.message ||
        err.message ||
        'Failed to save block quantity'
      );
    }
    setTableLoading(false);
  };

  return (
    <div className="p-4 min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 pb-28">
      <div className="text-2xl font-bold text-blue-800 mb-6 flex items-center gap-2">
        <FiEdit2 className="text-blue-600" /> Block Stock
      </div>

      {/* ... existing code ... */}

      {/* Dropdowns */}
      <div className="flex flex-wrap gap-4 mb-6 items-center">
        {/* Purpose Dropdown */}
        <div className="relative w-[250px] max-w-full">
          <div
            onClick={() => !loading && setPurposeDropdownOpen((prev) => !prev)}
            className={`px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center h-[50px] ${loading ? 'opacity-60 cursor-not-allowed' : ''
              }`}
          >
            <span className="truncate flex-1 text-left">
              {selected.purpose_id
                ? purposeSearch || 'Purpose Selected'
                : 'Select Purpose'}
            </span>
            <div className="flex items-center gap-2 ml-2">
              {selected.purpose_id && (
                <FiX
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected({
                      purpose_id: '',
                      state_id: '',
                      warehouse_id: '',
                    });
                    setPurposeSearch('');
                    setStateSearch('');
                    setWarehouseSearch('');
                  }}
                  className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                  title="Clear"
                />
              )}
              <span className="text-gray-500">
                {purposeDropdownOpen ? '▲' : '▼'}
              </span>
            </div>
          </div >
          {purposeDropdownOpen && (
            <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
              <input
                type="text"
                placeholder="Search Purpose..."
                value={purposeSearch}
                onChange={(e) => setPurposeSearch(e.target.value)}
                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
              />
              {purposes
                .filter((p) =>
                  (p?.name || '')
                    .toLowerCase()
                    .includes(purposeSearch.toLowerCase())
                )
                .map((p) => (
                  <div
                    key={p.id}
                    onClick={() => {
                      setSelected({
                        ...selected,
                        purpose_id: p.id,
                        state_id: '',
                        warehouse_id: '',
                      });
                      setPurposeSearch(p.name);
                      setPurposeDropdownOpen(false);
                      setStateSearch('');
                      setWarehouseSearch('');
                      setPage(1);
                    }}
                    className={`p-2 cursor-pointer hover:bg-blue-100 ${selected.purpose_id === p.id
                      ? 'bg-blue-200 font-semibold'
                      : ''
                      }`}
                  >
                    {p.name}
                  </div>
                ))}
            </div>
          )
          }
        </div >

        {/* State Dropdown */}
        < div className="relative w-[250px] max-w-full" >
          <div
            onClick={() =>
              selected.purpose_id &&
              !loading &&
              setStateDropdownOpen((prev) => !prev)
            }
            className={`px-4 py-2 border rounded-xl bg-white shadow-sm text-base flex justify-between items-center h-[50px] ${!selected.purpose_id || loading
              ? 'opacity-60 cursor-not-allowed'
              : 'cursor-pointer'
              }`}
          >
            <span className="truncate flex-1 text-left">
              {selected.state_id
                ? stateSearch || 'State Selected'
                : 'Select State'}
            </span>
            <div className="flex items-center gap-2 ml-2">
              {selected.state_id && (
                <FiX
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected({
                      ...selected,
                      state_id: '',
                      warehouse_id: '',
                    });
                    setStateSearch('');
                    setWarehouseSearch('');
                  }}
                  className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                  title="Clear"
                />
              )}
              <span className="text-gray-500">
                {stateDropdownOpen ? '▲' : '▼'}
              </span>
            </div>
          </div>
          {
            stateDropdownOpen && (
              <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
                <input
                  type="text"
                  placeholder="Search State..."
                  value={stateSearch}
                  onChange={(e) => setStateSearch(e.target.value)}
                  className="w-full px-3 py-2 border-b text-sm focus:outline-none"
                />
                {states
                  .filter((s) =>
                    (s?.name || '')
                      .toLowerCase()
                      .includes(stateSearch.toLowerCase())
                  )
                  .map((s) => (
                    <div
                      key={s.id}
                      onClick={() => {
                        setSelected({
                          ...selected,
                          state_id: s.id,
                          warehouse_id: '',
                        });
                        setStateSearch(s.name);
                        setStateDropdownOpen(false);
                        setWarehouseSearch('');
                        setPage(1);
                      }}
                      className={`p-2 cursor-pointer hover:bg-blue-100 ${selected.state_id === s.id
                        ? 'bg-blue-200 font-semibold'
                        : ''
                        }`}
                    >
                      {s.name}
                    </div>
                  ))}
              </div>
            )
          }
        </div >

        {/* Warehouse Dropdown */}
        < div className="relative w-[250px] max-w-full" >
          <div
            onClick={() =>
              selected.state_id &&
              !loading &&
              setWarehouseDropdownOpen((prev) => !prev)
            }
            className={`px-4 py-2 border rounded-xl bg-white shadow-sm text-base flex justify-between items-center h-[50px] ${!selected.state_id || loading
              ? 'opacity-60 cursor-not-allowed'
              : 'cursor-pointer'
              }`}
          >
            <span className="truncate flex-1 text-left">
              {selected.warehouse_id
                ? warehouseSearch || 'Warehouse Selected'
                : 'Select Warehouse'}
            </span>
            <div className="flex items-center gap-2 ml-2">
              {selected.warehouse_id && (
                <FiX
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected({ ...selected, warehouse_id: '' });
                    setWarehouseSearch('');
                  }}
                  className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                  title="Clear"
                />
              )}
              <span className="text-gray-500">
                {warehouseDropdownOpen ? '▲' : '▼'}
              </span>
            </div>
          </div>
          {
            warehouseDropdownOpen && (
              <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
                <input
                  type="text"
                  placeholder="Search Warehouse..."
                  value={warehouseSearch}
                  onChange={(e) => setWarehouseSearch(e.target.value)}
                  className="w-full px-3 py-2 border-b text-sm focus:outline-none"
                />
                {filteredWarehouses
                  .filter((w) =>
                    (w?.warehouse_name || '')
                      .toLowerCase()
                      .includes(warehouseSearch.toLowerCase())
                  )
                  .map((w) => (
                    <div
                      key={w.id}
                      onClick={() => {
                        setSelected({ ...selected, warehouse_id: w.id });
                        setWarehouseSearch(w.warehouse_name);
                        setWarehouseDropdownOpen(false);
                        setPage(1);
                      }}
                      className={`p-2 cursor-pointer hover:bg-blue-100 ${selected.warehouse_id === w.id
                        ? 'bg-blue-200 font-semibold'
                        : ''
                        }`}
                    >
                      {w.warehouse_name}
                    </div>
                  ))}
              </div>
            )
          }
        </div >
      </div >

      {/* Extra Filters: visible after warehouse selection */}
      {
        selected.warehouse_id && (
          <div className="flex flex-wrap gap-4 mb-6 items-center">
            {/* Commodity Dropdown */}
            <div className="relative w-[250px] max-w-full">
              <div
                onClick={() => setCommodityDropdownOpen((prev) => !prev)}
                className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center h-[50px]"
              >
                <span className="truncate flex-1 text-left">
                  {selectedCommodity
                    ? commoditySearch || 'Commodity Selected'
                    : 'Select Commodity'}
                </span>
                <div className="flex items-center gap-2 ml-2">
                  {selectedCommodity && (
                    <FiX
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCommodity('');
                        setCommoditySearch('');
                      }}
                      className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                      title="Clear"
                    />
                  )}
                  <span className="text-gray-500">
                    {commodityDropdownOpen ? '▲' : '▼'}
                  </span>
                </div>
              </div>
              {commodityDropdownOpen && (
                <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
                  <input
                    type="text"
                    placeholder="Search Commodity..."
                    value={commoditySearch}
                    onChange={(e) => setCommoditySearch(e.target.value)}
                    className="w-full px-3 py-2 border-b text-sm focus:outline-none"
                  />
                  {commodities
                    .filter((c) =>
                      (c?.commodityname || '')
                        .toLowerCase()
                        .includes(commoditySearch.toLowerCase())
                    )
                    .map((c) => (
                      <div
                        key={c.id}
                        onClick={() => {
                          setSelectedCommodity(c.id);
                          setCommoditySearch(c.commodityname);
                          setCommodityDropdownOpen(false);
                          setPage(1);
                        }}
                        className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedCommodity === c.id
                          ? 'bg-blue-200 font-semibold'
                          : ''
                          }`}
                      >
                        {c.commodityname}
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Scheme Dropdown */}
            <div className="relative w-[250px] max-w-full">
              <div
                onClick={() => setSchemeDropdownOpen((prev) => !prev)}
                className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center h-[50px]"
              >
                <span className="truncate flex-1 text-left">
                  {selectedScheme
                    ? schemeSearch || 'Scheme Selected'
                    : 'Select Scheme'}
                </span>
                <div className="flex items-center gap-2 ml-2">
                  {selectedScheme && (
                    <FiX
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedScheme('');
                        setSchemeSearch('');
                      }}
                      className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                      title="Clear"
                    />
                  )}
                  <span className="text-gray-500">
                    {schemeDropdownOpen ? '▲' : '▼'}
                  </span>
                </div>
              </div>
              {schemeDropdownOpen && (
                <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
                  <input
                    type="text"
                    placeholder="Search Scheme..."
                    value={schemeSearch}
                    onChange={(e) => setSchemeSearch(e.target.value)}
                    className="w-full px-3 py-2 border-b text-sm focus:outline-none"
                  />
                  {schemes
                    .filter((s) =>
                      (s?.name || '')
                        .toLowerCase()
                        .includes(schemeSearch.toLowerCase())
                    )
                    .map((s) => (
                      <div
                        key={s.id}
                        onClick={() => {
                          setSelectedScheme(s.id);
                          setSchemeSearch(s.name);
                          setSchemeDropdownOpen(false);
                          setPage(1);
                        }}
                        className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedScheme === s.id
                          ? 'bg-blue-200 font-semibold'
                          : ''
                          }`}
                      >
                        {s.name}
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Season Dropdown */}
            <div className="relative w-[250px] max-w-full">
              <div
                onClick={() => setSeasonDropdownOpen((prev) => !prev)}
                className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center h-[50px]"
              >
                <span className="truncate flex-1 text-left">
                  {selectedSeason
                    ? seasonSearch || 'Season Selected'
                    : 'Select Season'}
                </span>
                <div className="flex items-center gap-2 ml-2">
                  {selectedSeason && (
                    <FiX
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSeason('');
                        setSeasonSearch('');
                      }}
                      className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                      title="Clear"
                    />
                  )}
                  <span className="text-gray-500">
                    {seasonDropdownOpen ? '▲' : '▼'}
                  </span>
                </div>
              </div>
              {seasonDropdownOpen && (
                <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
                  <input
                    type="text"
                    placeholder="Search Season..."
                    value={seasonSearch}
                    onChange={(e) => setSeasonSearch(e.target.value)}
                    className="w-full px-3 py-2 border-b text-sm focus:outline-none"
                  />
                  {seasons
                    .filter((s) =>
                      (s?.name || '')
                        .toLowerCase()
                        .includes(seasonSearch.toLowerCase())
                    )
                    .map((s) => (
                      <div
                        key={s.id}
                        onClick={() => {
                          setSelectedSeason(s.id);
                          setSeasonSearch(s.name);
                          setSeasonDropdownOpen(false);
                          setPage(1);
                        }}
                        className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedSeason === s.id
                          ? 'bg-blue-200 font-semibold'
                          : ''
                          }`}
                      >
                        {s.name}
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* WHR ID Filter */}
            <div className="relative w-[250px] max-w-full">
              <input
                type="text"
                placeholder="Search WHR ID..."
                value={selected.whr_id}
                onChange={(e) => {
                  setSelected({ ...selected, whr_id: e.target.value });
                  setPage(1);
                }}
                className="w-full px-4 py-2 border rounded-xl bg-white shadow-sm text-base h-[50px] focus:ring-2 focus:ring-blue-500 outline-none transition"
              />
              {selected.whr_id && (
                <FiX
                  onClick={() => {
                    setSelected({ ...selected, whr_id: '' });
                    setPage(1);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                  title="Clear"
                />
              )}
            </div>
          </div>
        )
      }

      {/* Error */}
      {
        error && (
          <div className="mb-6 flex items-center gap-2 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg text-base font-semibold">
            <FiAlertCircle className="text-xl" />
            {error}
          </div>
        )
      }

      {/* Table Error */}
      {
        tableError && (
          <div className="mt-4 flex items-center gap-2 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg text-base font-semibold">
            <FiAlertCircle className="text-xl" />
            {tableError}
          </div>
        )
      }

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl shadow-xl border border-gray-100 bg-white">
        <table className="w-full text-left border-collapse min-w-[900px]">
          <thead className="bg-gray-100 border-b border-gray-200">
            <tr>
              <th className="py-4 px-6 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={tableData.length > 0 && selectedRows.size === tableData.length}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
              </th>
              {[
                'WHR ID',
                'WHR Date',
                'Bags (Available)',
                'Available Quantity(MT)',
                'Warehouse',
                'Commodity',
                'Scheme',
                'Block Qty(MT)',
                'Block Bags(Nos)',
                'Action',
              ].map((header) => (
                <th
                  key={header}
                  className="py-4 px-6 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {tableLoading ? (
              <tr>
                <td
                  colSpan={9}
                  className="text-center py-10 text-blue-600 text-lg font-semibold"
                >
                  <div className="flex justify-center items-center gap-2">
                    <FiLoader className="animate-spin text-2xl" />
                    <span>Loading...</span>
                  </div>
                </td>
              </tr>
            ) : !selected.warehouse_id ? (
              <tr>
                <td
                  colSpan={9}
                  className="text-center py-12 text-gray-500 font-medium text-lg"
                >
                  <div className="flex flex-col items-center gap-2">
                    <FiAlertCircle className="text-3xl text-gray-400" />
                    <span>Please select Purpose, State, and Warehouse to view stock.</span>
                  </div>
                </td>
              </tr>
            ) : tableData.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  className="text-center py-12 text-gray-500 font-medium text-lg"
                >
                  <div className="flex flex-col items-center gap-2">
                    <FiAlertCircle className="text-3xl text-gray-400" />
                    <span>No data found</span>
                  </div>
                </td>
              </tr>
            ) : (
              tableData.map((row) => (
                <tr
                  key={row.id}
                  className="hover:bg-gray-50 transition-colors duration-200"
                >
                  <td className="py-4 px-6">
                    <input
                      type="checkbox"
                      checked={selectedRows.has(row.id)}
                      onChange={() => handleSelectRow(row.id)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </td>
                  <td className="py-4 px-6 text-sm font-medium text-gray-900">{row.whr_id}</td>
                  <td className="py-4 px-6 text-sm text-gray-700">{row.whr_date}</td>
                  <td className="py-4 px-6 text-sm text-gray-700">
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500">Actual: {row.bags}</span>
                      <span className="font-bold text-green-700">Available: {Number(row.current_bags) === 0 && Number(row.blocked_qty) === 0 ? row.bags : row.current_bags}</span>
                    </div>
                  </td>
                  <td className="py-4 px-6 text-sm">
                    <span className="inline-block px-2 py-1 rounded-lg bg-green-50 text-green-700 font-bold border border-green-100">
                      {row.remaining_qty}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-sm font-medium text-gray-800">
                    {row.warehouse?.warehouse_name}
                  </td>
                  <td className="py-4 px-6 text-sm text-blue-700 font-medium">
                    {row.commodity?.commodityname}
                  </td>
                  <td className="py-4 px-6 text-sm">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                      {row.scheme?.scheme_name}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-sm text-gray-700">
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={editRows[row.id]?.block_qty ?? ''}
                      onChange={(e) => handleEdit(row.id, 'block_qty', e.target.value)}
                      className={`w-24 px-3 py-1.5 border rounded-lg text-sm focus:ring-2 outline-none transition bg-gray-50 ${(parseFloat(editRows[row.id]?.block_qty || 0) > parseFloat(row.remaining_qty || 0))
                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                      placeholder="Qty"
                    />
                  </td>
                  <td className="py-4 px-6 text-sm text-gray-700">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={editRows[row.id]?.blocked_bags ?? ''}
                      onChange={(e) => handleEdit(row.id, 'blocked_bags', e.target.value)}
                      className={`w-24 px-3 py-1.5 border rounded-lg text-sm focus:ring-2 outline-none transition bg-gray-50 ${(parseFloat(editRows[row.id]?.blocked_bags || 0) > parseFloat(row.bags || 0))
                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                      placeholder="Bags"
                    />
                  </td>
                  <td className="py-4 px-6 text-sm">
                    <button
                      onClick={() => handleSave(row)}
                      className="flex items-center gap-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow-sm text-xs font-semibold uppercase tracking-wide transition hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={
                        tableLoading ||
                        !editRows[row.id]?.block_qty ||
                        parseFloat(editRows[row.id]?.block_qty || 0) > parseFloat(row.remaining_qty || 0) ||
                        parseFloat(editRows[row.id]?.blocked_bags || 0) > parseFloat(row.bags || 0)
                      }
                    >
                      <FiSave /> Save
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {selected.warehouse_id && total > 0 && (
        <div className="mt-8 flex flex-col md:flex-row justify-between items-center gap-4 p-4 bg-white rounded-xl shadow-md border border-gray-100">
          {/* Record Summary */}
          <div className="text-sm text-gray-600 font-medium order-2 md:order-1">
            Showing <span className="text-blue-600 font-bold">{Math.min(total, (page - 1) * limit + 1)}</span> to{' '}
            <span className="text-blue-600 font-bold">{Math.min(total, page * limit)}</span> of{' '}
            <span className="text-blue-600 font-bold">{total}</span> records
          </div>

          {/* Page Controls */}
          <div className="flex items-center gap-4 order-1 md:order-2">
            <div className="flex items-center gap-2">
              <button
                disabled={page === 1 || tableLoading}
                onClick={() => fetchTableData(page - 1)}
                className="flex items-center gap-1 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition font-bold"
              >
                <FiChevronLeft className="text-lg" /> Prev
              </button>

              <div className="flex items-center gap-1 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                <span className="text-gray-500 text-xs font-bold uppercase">Page</span>
                <span className="text-blue-700 font-black px-1">{page}</span>
                <span className="text-gray-400 font-medium">/</span>
                <span className="text-gray-500 font-bold">{totalPages}</span>
              </div>

              <button
                disabled={page === totalPages || tableLoading}
                onClick={() => fetchTableData(page + 1)}
                className="flex items-center gap-1 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition font-bold"
              >
                Next <FiChevronRight className="text-lg" />
              </button>
            </div>
          </div>

          {/* Rows per page */}
          <div className="flex items-center gap-3 order-3">
            <span className="text-gray-500 text-xs font-bold uppercase tracking-wider whitespace-nowrap">Records per page:</span>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              className="px-3 py-2 border border-blue-100 rounded-lg bg-blue-50/50 text-blue-700 font-bold cursor-pointer hover:bg-white hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all outline-none"
            >
              {[10, 25, 50, 100].map(val => (
                <option key={val} value={val}>{val}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Footer Summary Bar */}
      {selectedRows.size > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white border border-gray-200 shadow-2xl rounded-xl p-4 z-50 min-w-[600px] animate-fade-in-up">
          <div className="flex justify-between items-center gap-8 px-2">
            <div className="flex items-center gap-3">
              <span className="bg-blue-600 text-white w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold shadow-md">
                {selectedRows.size}
              </span>
              <span className="text-gray-700 font-semibold text-lg">Items Selected</span>
            </div>

            <div className="flex items-center gap-6">
              <div className="h-10 w-px bg-gray-200"></div>
              <div className="text-right">
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-0.5">Total Qty</p>
                <p className="text-xl font-black text-gray-800 leading-none">
                  {calculateTotalSelectedQty()} <span className="text-sm font-medium text-gray-400">MT</span>
                </p>
              </div>

              <div className="text-right border-l pl-6 border-gray-200">
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-0.5">Total Bags</p>
                <p className="text-xl font-black text-gray-800 leading-none">
                  {calculateTotalSelectedBags()} <span className="text-sm font-medium text-gray-400">Nos</span>
                </p>
              </div>

              <button
                className="px-6 py-2.5 bg-gray-900 hover:bg-black text-white font-bold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                onClick={handleBulkBlock}
                disabled={bulkLoading}
              >
                {bulkLoading ? (
                  <>
                    <FiLoader className="animate-spin text-lg" /> Processing...
                  </>
                ) : (
                  'Proceed to Block'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Results Modal */}
      {showBulkModal && bulkResults && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col animate-fade-in-up">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="text-xl font-bold text-gray-800">Block Stock Results</h3>
              <button
                onClick={() => setShowBulkModal(false)}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
              >
                <FiX className="text-gray-500 text-xl" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              {/* Success Section */}
              {bulkResults.success?.length > 0 && (
                <div className="mb-6">
                  <h4 className="flex items-center gap-2 text-green-700 font-bold mb-3">
                    <FiCheck className="text-xl" />
                    Successfully Blocked ({bulkResults.success.length})
                  </h4>
                  <div className="bg-green-50 rounded-xl border border-green-100 overflow-hidden">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-green-100/50 text-green-800 font-semibold">
                        <tr>
                          <th className="px-4 py-2">WHR ID</th>
                          <th className="px-4 py-2">Qty Blocked</th>
                          <th className="px-4 py-2">Message</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-green-100">
                        {bulkResults.success.map((item, idx) => (
                          <tr key={idx} className="hover:bg-green-100/30">
                            <td className="px-4 py-2 font-medium">{item.whr_id}</td>
                            <td className="px-4 py-2">{item.blocked_qty}</td>
                            <td className="px-4 py-2">{item.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Failed Section */}
              {bulkResults.failed?.length > 0 && (
                <div>
                  <h4 className="flex items-center gap-2 text-red-700 font-bold mb-3">
                    <FiAlertCircle className="text-xl" />
                    Failed to Block ({bulkResults.failed.length})
                  </h4>
                  <div className="bg-red-50 rounded-xl border border-red-100 overflow-hidden">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-red-100/50 text-red-800 font-semibold">
                        <tr>
                          <th className="px-4 py-2">WHR ID</th>
                          <th className="px-4 py-2">Error</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-red-100">
                        {bulkResults.failed.map((item, idx) => (
                          <tr key={idx} className="hover:bg-red-100/30">
                            <td className="px-4 py-2 font-medium">{item.whr_id}</td>
                            <td className="px-4 py-2">{item.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end">
              <button
                onClick={() => setShowBulkModal(false)}
                className="px-6 py-2 bg-gray-800 hover:bg-gray-900 text-white font-semibold rounded-lg shadow-sm transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div >
  );
}
